/**
 * Web adapter for react-native-sound using HTML5 Audio element.
 */
export default class SoundWeb {
    private audio: HTMLAudioElement | null = null;
    private onEnd: ((success: boolean) => void) | null = null;

    static setCategory(_category: string) {
        // No-op on web
    }

    constructor(url: string, _basePath: string, callback?: (error: any) => void) {
        try {
            this.audio = new Audio(url);
            
            // Listen for canplaythrough or error
            const handleCanPlay = () => {
                this.audio?.removeEventListener('canplaythrough', handleCanPlay);
                this.audio?.removeEventListener('error', handleError);
                callback?.(null);
            };

            const handleError = (e: Event) => {
                this.audio?.removeEventListener('canplaythrough', handleCanPlay);
                this.audio?.removeEventListener('error', handleError);
                console.warn('[SoundWeb] Audio load error:', e);
                callback?.(e || new Error('Failed to load audio'));
            };

            this.audio.addEventListener('canplaythrough', handleCanPlay);
            this.audio.addEventListener('error', handleError);
            
            // Preload
            this.audio.load();
        } catch (err) {
            console.warn('[SoundWeb] Constructor exception:', err);
            callback?.(err);
        }
    }

    play(onEnd?: (success: boolean) => void) {
        if (!this.audio) {
            onEnd?.(false);
            return;
        }

        this.onEnd = onEnd || null;

        const handleEnded = () => {
            this.audio?.removeEventListener('ended', handleEnded);
            this.audio?.removeEventListener('error', handleError);
            if (this.onEnd) {
                this.onEnd(true);
                this.onEnd = null;
            }
        };

        const handleError = () => {
            this.audio?.removeEventListener('ended', handleEnded);
            this.audio?.removeEventListener('error', handleError);
            if (this.onEnd) {
                this.onEnd(false);
                this.onEnd = null;
            }
        };

        this.audio.addEventListener('ended', handleEnded);
        this.audio.addEventListener('error', handleError);

        this.audio.play().catch((err) => {
            console.warn('[SoundWeb] Play error (autoplay blocked or network error):', err);
            handleError();
        });
    }

    stop(onStop?: () => void) {
        if (this.audio) {
            this.audio.pause();
            this.audio.currentTime = 0;
        }
        if (this.onEnd) {
            this.onEnd(false);
            this.onEnd = null;
        }
        onStop?.();
    }

    release() {
        if (this.audio) {
            this.audio.pause();
            this.audio.src = '';
            this.audio = null;
        }
    }
}
